import { getTranslations } from '../services/api';
import debug from './debug';

// Supported language codes
export const SUPPORTED_LANGUAGES = [
  'en', // English (default)
  'zh', // Mandarin Chinese
  'hi', // Hindi
  'es', // Spanish
  'fr', // French
  'ar', // Standard Arabic
  'bn', // Bengali
  'pt', // Portuguese
  'ru', // Russian
  'ur'  // Urdu
];

// Country code to language mapping (simplified)
const COUNTRY_LANGUAGE_MAP: Record<string, string> = {
  'US': 'en', 'GB': 'en', 'AU': 'en', 'CA': 'en',
  'CN': 'zh', 'HK': 'zh', 'TW': 'zh', 'SG': 'zh',
  'IN': 'hi', // Note: India has many languages
  'ES': 'es', 'MX': 'es', 'AR': 'es', 'CO': 'es',
  'FR': 'fr', 'BE': 'fr', 'CH': 'fr',
  'SA': 'ar', 'AE': 'ar', 'EG': 'ar', 'MA': 'ar',
  'BD': 'bn',
  'PT': 'pt', 'BR': 'pt',
  'RU': 'ru', 'KZ': 'ru', 'BY': 'ru',
  'PK': 'ur'
};

// Detect language based on browser settings and location
export const detectUserLanguage = async (): Promise<string> => {
  debug.log('Detecting user language...');
  
  // HIGHEST PRIORITY: Check browser language
  const browserLang = navigator.language.split('-')[0];
  debug.log(`Browser language detected: ${browserLang}`);
  
  if (SUPPORTED_LANGUAGES.includes(browserLang)) {
    debug.log(`Browser language ${browserLang} is supported, using it`);
    return browserLang;
  }
  
  // SECOND PRIORITY: Check if a language preference is stored
  const storedLanguage = localStorage.getItem('preferredLanguage');
  if (storedLanguage && SUPPORTED_LANGUAGES.includes(storedLanguage)) {
    debug.log(`Using stored language preference: ${storedLanguage}`);
    return storedLanguage;
  }
  
  // THIRD PRIORITY: Try geolocation-based detection
  try {
    debug.log('Attempting geolocation-based language detection');
    const response = await fetch('https://ipapi.co/json/');
    const data = await response.json();
    const countryCode = data.country_code;
    
    if (countryCode && COUNTRY_LANGUAGE_MAP[countryCode]) {
      const geoLang = COUNTRY_LANGUAGE_MAP[countryCode];
      debug.log(`Geolocation detected language: ${geoLang} from country: ${countryCode}`);
      return geoLang;
    }
  } catch (error) {
    debug.warn('Geolocation detection failed:', error);
  }
  
  // Default to English
  debug.log('No supported language detected, defaulting to English');
  return 'en';
};

// Load translations for a specific language
export const loadTranslations = async (languageCode: string): Promise<Record<string, any> | null> => {
  try {
    debug.log(`Loading translations for language: ${languageCode}`);
    
    // First try to load from Firestore
    const langData = await getTranslations(languageCode);
    
    if (langData) {
      debug.log(`Loaded translations from Firestore for ${languageCode}`);
      return langData;
    }
    
    // Fallback to static JSON files
    debug.log(`Attempting to load translations from static file for ${languageCode}`);
    const response = await fetch(`/locales/${languageCode}.json?t=${new Date().getTime()}`);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch translations: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    debug.log(`Successfully loaded translations from file for ${languageCode}`);
    return data;
  } catch (error) {
    debug.error(`Failed to load translations for ${languageCode}:`, error);
    
    // Ultimate fallback - load English
    if (languageCode !== 'en') {
      debug.log(`Falling back to English translations`);
      return loadTranslations('en');
    }
    
    return null;
  }
};